import type { MutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai/index';
import { addressCreatedAtom, addressesLoadedAtom, invitedByAtom } from 'app/atoms';
import { useMyActivePlans } from 'app/network/plan';
import { useUser } from 'app/network/user';
import { http } from 'app/lib/axios';
import { usePreferences } from 'app/network/preferences';
import type { Address } from 'app/types/address';
import { addressKeys } from 'app/network/keys';
import { useQueryKeys } from './invalidation';
import { getAddressPayload } from './address-utils';
import { ADDRESS_REGION_CHANGE_ERROR, isAddressCountryAllowed } from 'app/lib/address-region';

export type CreateAddressDTO = Omit<Address, 'id' | 'userId' | '$signals' | '$signals_data' | 'radius'> & {
  radius?: number | null;
};

export type TemporaryAddressDTO = Pick<
  CreateAddressDTO,
  'postalCode' | 'country' | 'radius' | 'radiusCenterLatitude' | 'radiusCenterLongitude' | 'latitude' | 'longitude'
>;

export type UpdateAddressDTO = Partial<CreateAddressDTO & Pick<Address, 'radius' | 'latitude' | 'longitude'>> & {
  id: string;
};

async function getAccountAddresses(client: ReturnType<typeof useQueryClient>) {
  const queryKey = addressKeys.list('self');
  const addresses = (await http.get<Address[]>('/addresses')).data;
  client.setQueryData(queryKey, addresses);
  return addresses;
}

async function assertAddressRemainsInAccountRegion(
  client: ReturnType<typeof useQueryClient>,
  candidateCountry?: string | null,
  editedAddressId?: Address['id'],
) {
  if (!candidateCountry) return;

  const addresses = await getAccountAddresses(client);
  const editedAddress = addresses.find((address) => address.id === editedAddressId);
  if (editedAddress?.country?.toUpperCase() === candidateCountry.toUpperCase()) return;
  const accountCountry = addresses[0]?.country;

  if (!isAddressCountryAllowed(accountCountry, candidateCountry)) {
    throw new Error(ADDRESS_REGION_CHANGE_ERROR);
  }
}

async function assertAddressDeletionKeepsAccountRegion(
  client: ReturnType<typeof useQueryClient>,
  deletedAddressId: Address['id'],
) {
  const addresses = await getAccountAddresses(client);
  if (addresses[0]?.id !== deletedAddressId) return;

  const nextPrimaryAddress = addresses.find((address) => address.id !== deletedAddressId);
  if (nextPrimaryAddress && !isAddressCountryAllowed(addresses[0].country, nextPrimaryAddress.country)) {
    throw new Error(ADDRESS_REGION_CHANGE_ERROR);
  }
}

export function useCreateAddress(opts: MutationOptions<Address, Error, CreateAddressDTO> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setAddressesLoaded = useSetAtom(addressesLoadedAtom);
  const setAddressCreated = useSetAtom(addressCreatedAtom);
  const [invitedBy] = useAtom(invitedByAtom);
  const { refetch: refetchPlan } = useMyActivePlans();
  const { refetch: refetchUser } = useUser();
  const { refetch: refetchPreferences } = usePreferences();

  return useMutation<Address, Error, CreateAddressDTO>({
    mutationFn: async (newAddress) => {
      await assertAddressRemainsInAccountRegion(client, newAddress.country);
      const res = await http.post<Address>('/addresses', getAddressPayload(newAddress, invitedBy));
      return res.data;
    },
    ...opts,
    async onSuccess(result, variables, context) {
      // Prevent useAllAddresses above from triggering the onboarding flow again as soon as the address is created
      setAddressCreated(true);

      await qk.myAddresses.invalidate();
      setAddressesLoaded(false);
      client.setQueryData(addressKeys.detail(result.id), result);

      await refetchPlan();
      await refetchUser();
      await refetchPreferences();

      return opts.onSuccess?.(result, variables, context);
    },
  });
}
export function useUpdateAddress(opts: MutationOptions<Address, Error, UpdateAddressDTO> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setAddressesLoaded = useSetAtom(addressesLoadedAtom);
  const { refetch: refetchPlan } = useMyActivePlans();
  const { refetch: refetchUser } = useUser();

  return useMutation<Address, Error, UpdateAddressDTO>({
    mutationFn: async (updatedAddress) => {
      await assertAddressRemainsInAccountRegion(client, updatedAddress.country, updatedAddress.id);
      return http
        .patch<Address>(`/addresses/${updatedAddress.id}`, getAddressPayload(updatedAddress))
        .then((res) => res.data);
    },
    ...opts,
    async onSuccess(result, variables, context) {
      await qk.afterAddressChanged();
      setAddressesLoaded(false);
      client.setQueryData(addressKeys.detail(result.id), result);
      await refetchPlan();
      await refetchUser();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useDeleteAddress(opts: MutationOptions<Address, Error, Address['id']> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setAddressesLoaded = useSetAtom(addressesLoadedAtom);
  const { refetch: refetchPlan } = useMyActivePlans();
  const { refetch: refetchUser } = useUser();

  return useMutation<Address, Error, Address['id']>({
    mutationFn: async (id: Address['id']) => {
      await assertAddressDeletionKeepsAccountRegion(client, id);
      return http.delete<Address>(`/addresses/${id}`).then((res) => res.data);
    },
    ...opts,
    async onSuccess(result, variables, context) {
      await qk.afterAddressChanged();
      setAddressesLoaded(false);
      await refetchPlan();
      await refetchUser();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}
